import React, { useEffect, useState, useMemo } from "react";
import { apiRequest } from "../../../../service/apiRequest";
import { Breadcrumb, Spin, Tag, Button, Select, Table, Modal, Space } from "antd";
import { HomeOutlined, CheckCircleFilled, DownloadOutlined, FileExcelOutlined, DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { calculateCFT, getWidthQtyTotal } from "../../../../utils/cftCalculator";
import { formatPrice } from "../../../../utils/formatPrice";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const DEPARTMENTS = [
  { key: "all", label: "All Departments", isTimber: false },
  { key: "machinery", label: "Machinery", isTimber: false, showTimber: true },
  { key: "assembly", label: "Assembly", isTimber: false },
  { key: "sanding", label: "Sanding", isTimber: false },
  { key: "finishing", label: "Finishing", isTimber: false },
  { key: "packed", label: "Packed", isTimber: false },
];

interface PIDetailItem {
  pid_id: number;
  pid_product_id: number;
  product_name: string;
  total_qty: number;
  ready_qty: number;
  purQty?: number;
  pur_qty?: number;
  pid_qty?: number;
}

interface PIDetail {
  pi_id: number;
  pi_order_no: string;
  pi_date: string;
  pi_lead_time: number;
  ledger_company_name: string;
  ledger_first_name: string;
  ledger_last_name: string;
  items: PIDetailItem[];
  issuedStore?: any[];
  issuedTimber?: any[];
  workOrders?: any[];
}

const getIssuedColor = (issuedQty: number, reqQty: number): string => {
  if (issuedQty <= 0) return "#8c8c8c";
  if (reqQty <= 0) return "#ff4d4f";
  const percent = parseFloat(((issuedQty / reqQty) * 100).toFixed(4));
  if (percent < 100) {
    return "#faad14"; // Yellow
  }
  if (percent <= 103) {
    return "#52c41a"; // Green
  }
  return "#ff4d4f"; // Red
};

const TrackPIDetail: React.FC = () => {
  const { pi_id } = useParams<{ pi_id: string }>();
  const [detail, setDetail] = useState<PIDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Requirements state
  const [boms, setBoms] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [woods, setWoods] = useState<any[]>([]);
  const [woodTypes, setWoodTypes] = useState<any[]>([]);
  const [selectedDept, setSelectedDept] = useState<string>("all");
  const [downloadModalVisible, setDownloadModalVisible] = useState<boolean>(false);

  // New States
  const [employees, setEmployees] = useState<any[]>([]);
  const [allDepts, setAllDepts] = useState<any[]>([]);
  const [poBills, setPoBills] = useState<any[]>([]);
  const [removedRowKeys, setRemovedRowKeys] = useState<Set<string>>(new Set());

  // Modal Visibility States
  const [requirementModalVisible, setRequirementModalVisible] = useState<boolean>(false);
  const [trackPurchaseModalVisible, setTrackPurchaseModalVisible] = useState<boolean>(false);
  const [trackIssueModalVisible, setTrackIssueModalVisible] = useState<boolean>(false);
  const [trackWorkOrderModalVisible, setTrackWorkOrderModalVisible] = useState<boolean>(false);

  const handleRemoveRow = (key: string) => {
    setRemovedRowKeys(prev => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  const handleResetRows = () => {
    setRemovedRowKeys(new Set());
  };

  useEffect(() => {
    (async () => {
      try {
        const [bomRes, issueRes, woodRes, woodTypeRes, empRes, deptRes, poRes] = await Promise.all([
          apiRequest<any[]>({ method: "get", url: "/get-bom" }).catch(() => []),
          apiRequest<any[]>({ method: "get", url: "/issue-master" }).catch(() => []),
          apiRequest<any[]>({ method: "get", url: "/woods" }).catch(() => []),
          apiRequest<any[]>({ method: "get", url: "/wood-types" }).catch(() => []),
          apiRequest<any[]>({ method: "get", url: "/employees?status=1" }).catch(() => []),
          apiRequest<any[]>({ method: "get", url: "/departments?status=1" }).catch(() => []),
          apiRequest<any[]>({ method: "get", url: "/get-po-bill" }).catch(() => []),
        ]);
        setBoms(bomRes || []);
        setIssues(issueRes || []);
        setWoods(woodRes || []);
        setWoodTypes(woodTypeRes || []);
        setEmployees(empRes || []);
        setAllDepts(deptRes || []);
        setPoBills(poRes || []);
      } catch (error) {
        console.error("Fetch requirements data error:", error);
      }
    })();
  }, []);

  useEffect(() => {
    if (!pi_id) return;
    (async () => {
      try {
        setLoading(true);
        const data = await apiRequest<PIDetail>({
          method: "get",
          url: `/get-track-pi-detail/${pi_id}`,
        });
        setDetail(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [pi_id]);

  const matchedPoBills = useMemo(() => {
    if (!detail || !poBills) return [];
    const piNo = String(detail.pi_order_no).toLowerCase();
    return poBills.filter((bill: any) => {
      const poNo = String(bill.pbill_po_no || '').toLowerCase();
      return poNo.includes(piNo);
    });
  }, [detail, poBills]);

  const workOrdersByDept = useMemo(() => {
    if (!detail || !detail.workOrders) return {};
    const groups: { [key: number]: any[] } = {};
    detail.workOrders.forEach((wo: any) => {
      const emp = employees.find((e: any) => e.employee_id === wo.wo_employee_id);
      if (emp && emp.employee_department) {
        const deptIds = String(emp.employee_department).split(',').map(id => parseInt(id.trim(), 10)).filter(id => !isNaN(id));
        deptIds.forEach(deptId => {
          if (!groups[deptId]) {
            groups[deptId] = [];
          }
          groups[deptId].push(wo);
        });
      } else {
        const deptId = 0;
        if (!groups[deptId]) {
          groups[deptId] = [];
        }
        groups[deptId].push(wo);
      }
    });
    return groups;
  }, [detail, employees]);

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!detail) {
    return <div style={{ color: "#aaa", padding: 40 }}>PI not found</div>;
  }

  const deadline = dayjs(detail.pi_date).add(detail.pi_lead_time, "day");
  const daysLeft = deadline.diff(dayjs(), "day");
  const borderColor =
    daysLeft < 0 ? "#ff4d4f" : daysLeft <= 3 ? "#faad14" : "#52c41a";
  const tagColor = daysLeft < 0 ? "red" : daysLeft <= 3 ? "orange" : "green";
  const statusText =
    daysLeft < 0
      ? `${Math.abs(daysLeft)} days overdue`
      : daysLeft === 0
        ? "Due today"
        : `${daysLeft} days left`;

  // Helper to compute issued data for timber
  const getIssuedDataForTimber = (
    woodId: number,
    woodTypeId: number,
    length: number,
    thickness: number
  ) => {
    const matched = (detail?.issuedTimber || []).filter((it: any) => {
      const itWoodId = Number(it.wood_id || it.tiod_wood_id || it.woodId);
      const itWoodTypeId = Number(it.wood_type_id || it.tiod_wood_type_id || it.woodTypeId);
      return itWoodId === Number(woodId) && itWoodTypeId === Number(woodTypeId);
    });

    const issuedWidths: { [w: string]: number } = {};
    let totalIssuedCft = 0;
    let totalIssuedPcs = 0;

    matched.forEach((it: any) => {
      // 1. Check direct CFT field
      if (it.cft !== undefined && it.cft !== null && !isNaN(Number(it.cft))) {
        totalIssuedCft += Number(it.cft);
      } else if (it.issued_cft !== undefined && it.issued_cft !== null && !isNaN(Number(it.issued_cft))) {
        totalIssuedCft += Number(it.issued_cft);
      } else if (it.tiod_cft !== undefined && it.tiod_cft !== null && !isNaN(Number(it.tiod_cft))) {
        totalIssuedCft += Number(it.tiod_cft);
      }

      // 2. Width breakdown
      let rawWidths = it.widths || it.tiod_widths || it.wood_widths;
      if (typeof rawWidths === "string") {
        try {
          rawWidths = JSON.parse(rawWidths);
        } catch {
          rawWidths = null;
        }
      }

      if (rawWidths && typeof rawWidths === "object") {
        Object.entries(rawWidths).forEach(([w, q]) => {
          const numQ = Number(q) || 0;
          if (numQ > 0) {
            issuedWidths[String(w)] = (issuedWidths[String(w)] || 0) + numQ;
            totalIssuedPcs += numQ;
          }
        });
      } else if (it.width !== undefined || it.tiod_width !== undefined) {
        const w = String(it.width ?? it.tiod_width);
        const q = Number(it.issued_pcs ?? it.qty ?? it.tiod_qty ?? 0);
        if (q > 0) {
          issuedWidths[w] = (issuedWidths[w] || 0) + q;
          totalIssuedPcs += q;
        }
      } else if (it.issued_pcs || it.qty) {
        totalIssuedPcs += Number(it.issued_pcs || it.qty || 0);
      }
    });

    if (totalIssuedCft === 0 && Object.keys(issuedWidths).length > 0) {
      const widthQtyTotal = getWidthQtyTotal(issuedWidths);
      totalIssuedCft = calculateCFT(widthQtyTotal, length, thickness);
    } else if (totalIssuedCft === 0 && totalIssuedPcs > 0) {
      totalIssuedCft = calculateCFT(totalIssuedPcs, length, thickness);
    }

    return {
      issuedWidths,
      totalIssuedCft: parseFloat(totalIssuedCft.toFixed(4)),
      totalIssuedPcs,
    };
  };

  // Compute requirements for a specific department object
  const computeDeptData = (deptObj: typeof DEPARTMENTS[number]) => {
    if (!detail || !detail.items) return { requirementsData: [], activeWidthKeys: [], totalAmountSum: 0 };

    const rows: any[] = [];
    const widthSet = new Set<string>();

    detail.items.forEach((item, itemIdx) => {
      const purQty = Number((item as any).purQty ?? (item as any).pur_qty ?? (item as any).pid_qty ?? item.total_qty ?? 0);
      const bom = boms.find(
        (b) => Number(b.bom_product_id) === Number(item.pid_product_id)
      );

      if (deptObj.isTimber) {
        if (bom && bom.components && bom.components.length > 0) {
          bom.components.forEach((comp: any, cIdx: number) => {
            const compQty = Number(comp.component_qty) || 1;
            const multiplier = compQty * purQty;

            (comp.woodRows || []).forEach((wRow: any, wIdx: number) => {
              let rawWidthsObj = wRow.widths || wRow.bwr_widths || {};
              if (typeof rawWidthsObj === 'string') {
                try {
                  rawWidthsObj = JSON.parse(rawWidthsObj);
                } catch (e) {
                  rawWidthsObj = {};
                }
              }
              const actualWidthsObj: { [key: string]: number } = {};
              let mixQtyFromWidth1 = 0;

              Object.entries(rawWidthsObj).forEach(([w, q]) => {
                if (String(w) === "1") {
                  mixQtyFromWidth1 = Number(q) || 0;
                } else {
                  actualWidthsObj[w] = Number(q) || 0;
                  if (Number(q) > 0) widthSet.add(String(w));
                }
              });

              const woodMaster = woods.find(
                (w) => Number(w.wood_id) === Number(wRow.bwr_wood_id)
              );
              const woodTypeObj = woodTypes.find(
                (wt) => Number(wt.wood_id) === Number(wRow.bwr_wood_type_id)
              );

              const length = woodMaster ? Number(woodMaster.wood_length) : 5.25;
              const thickness = woodMaster ? Number(woodMaster.wood_thickness) : 1.5;
              const typeLabel = woodTypeObj?.wood_name
                ? woodTypeObj.wood_name.charAt(0).toUpperCase()
                : woodMaster?.wood_name || "S";

              const sizeWoodType = `${length}' x ${thickness}" (${typeLabel})`;

              // Handle 24-inch length (2.0 feet)
              const is24Inch = length === 2;
              const mixQty = is24Inch ? 24 : (mixQtyFromWidth1 || Number(wRow.mix) || Number(wRow.mix_qty) || 0);

              const widthQtyTotal = getWidthQtyTotal(rawWidthsObj);
              const unitCft = calculateCFT(widthQtyTotal, length, thickness);
              const totalCft = parseFloat((unitCft * compQty * purQty).toFixed(4));
              const price = Number(wRow.bwr_rate) || 0;
              const totalAmount = parseFloat((totalCft * price).toFixed(4));

              const sumWidthPcs = Object.values(actualWidthsObj).reduce((sum: number, q: any) => sum + (Number(q) || 0), 0);
              const basePcs = is24Inch ? 24 : (sumWidthPcs > 0 ? sumWidthPcs : (mixQty || 100));
              const totalPcs = basePcs * multiplier;

              const scaledWidths = Object.fromEntries(
                Object.entries(actualWidthsObj).map(([w, q]) => [w, Number(q) * multiplier])
              );

              // Find matched issue in detail.issuedTimber
              const { issuedWidths, totalIssuedCft } = getIssuedDataForTimber(
                Number(wRow.bwr_wood_id),
                Number(wRow.bwr_wood_type_id),
                length,
                thickness
              );
              const issuedVal = totalIssuedCft > 0 ? `${totalIssuedCft} cft` : "0";

              rows.push({
                key: `${itemIdx}-${cIdx}-${wIdx}`,
                size_wood_type: sizeWoodType,
                mix: mixQty > 0 ? mixQty * multiplier : (sumWidthPcs > 0 ? "" : 100 * multiplier),
                widths: scaledWidths,
                issued_widths: issuedWidths,
                cft: totalCft,
                pcs: totalPcs,
                price: price,
                total_amount: totalAmount,
                issued_item: issuedVal,
                issued_cft: totalIssuedCft,
                wood_id: wRow.bwr_wood_id,
                wood_type_id: wRow.bwr_wood_type_id,
                is_timber: true,
              });
            });
          });
        } else {
          const estCft = parseFloat((1.5 * purQty).toFixed(4));
          rows.push({
            key: `def-${itemIdx}`,
            size_wood_type: "5.25' x 1.5\" (S)",
            mix: 100 * purQty,
            widths: {},
            issued_widths: {},
            cft: estCft,
            pcs: purQty * 100,
            price: 0,
            total_amount: 0,
            issued_item: "0",
            issued_cft: 0,
            is_timber: true,
          });
        }
      } else {
        const deptLabelLower = (deptObj.label || "").toLowerCase();
        let secKey = "Machinery";
        if (deptLabelLower.includes("assembly")) secKey = "Assembly";
        else if (deptLabelLower.includes("sanding")) secKey = "Sanding";
        else if (deptLabelLower.includes("finishing")) secKey = "Finishing";
        else if (deptLabelLower.includes("pack")) secKey = "Packed";
        else if (deptLabelLower.includes("machin")) secKey = "Machinery";
        else {
          const matchedKey = bom ? Object.keys(bom).find((k) => k.toLowerCase() === deptLabelLower) : null;
          secKey = matchedKey || deptObj.label;
        }

        const secRows = bom ? bom[secKey] || [] : [];

        if (secRows.length > 0) {
          secRows.forEach((sRow: any, sIdx: number) => {
            const prodName = sRow.product_name || sRow.bsr_product_name || sRow.bsr_section_name || item.product_name;

            const rawUnitQty = Number(sRow.bsr_qty) || 0;
            const scaledQty = parseFloat((rawUnitQty * purQty).toFixed(3));
            const price = Number(sRow.bsr_price) || 0;
            const totalAmount = parseFloat((scaledQty * price).toFixed(4));

            // Find matched issue in detail.issuedStore
            const matchedStoreIssue = (detail.issuedStore || []).find(
              (is: any) => Number(is.product_id) === Number(sRow.bsr_product_id || sRow.product_id)
            );
            const issuedQty = matchedStoreIssue ? Number(matchedStoreIssue.issued_qty || 0) : 0;

            rows.push({
              key: `sec-${itemIdx}-${sIdx}`,
              product_name: prodName,
              product_id: sRow.bsr_product_id || sRow.product_id,
              qty: scaledQty,
              unit_name: sRow.unit_name || sRow.ut_name || "",
              price: price,
              total_amount: totalAmount,
              issued_item: issuedQty > 0 ? `${issuedQty}` : "0",
              is_timber: false,
            });
          });
        } else {
          rows.push({
            key: `sec-def-${itemIdx}`,
            product_name: item.product_name,
            product_id: item.pid_product_id,
            qty: purQty,
            price: 0,
            total_amount: 0,
            issued_item: "0",
            is_timber: false,
          });
        }
      }
    });

    // Merge same products/timber items within the department
    const finalRows: any[] = [];
    if (deptObj.isTimber) {
      const mergedMap = new Map<string, any>();
      rows.forEach((r, idx) => {
        const groupKey = `${r.size_wood_type}_${r.price}`;
        if (mergedMap.has(groupKey)) {
          const existing = mergedMap.get(groupKey);
          existing.cft = parseFloat((existing.cft + r.cft).toFixed(4));
          existing.pcs = existing.pcs + r.pcs;
          existing.total_amount = parseFloat((existing.total_amount + r.total_amount).toFixed(4));
          if (r.widths) {
            Object.entries(r.widths).forEach(([w, q]) => {
              if (String(w) !== "1") {
                existing.widths[w] = (Number(existing.widths[w]) || 0) + (Number(q) || 0);
                if (Number(q) > 0) widthSet.add(String(w));
              }
            });
          }
          if (r.issued_widths) {
            existing.issued_widths = { ...existing.issued_widths, ...r.issued_widths };
          }
          if (typeof r.mix === "number") {
            existing.mix = (Number(existing.mix) || 0) + r.mix;
          }
        } else {
          mergedMap.set(groupKey, {
            ...r,
            key: `timber-merged-${idx}`,
            widths: { ...r.widths },
            issued_widths: { ...(r.issued_widths || {}) },
          });
        }
      });
      finalRows.push(...Array.from(mergedMap.values()));
    } else {
      const mergedMap = new Map<string, any>();
      rows.forEach((r, idx) => {
        const pName = r.product_name;
        if (mergedMap.has(pName)) {
          const existing = mergedMap.get(pName);
          existing.qty += r.qty;
          existing.total_amount = parseFloat((existing.total_amount + r.total_amount).toFixed(4));
        } else {
          mergedMap.set(pName, {
            ...r,
            key: `sec-merged-${idx}`,
          });
        }
      });
      finalRows.push(...Array.from(mergedMap.values()));
    }

    const activeWidthKeys = Array.from(widthSet).sort((a, b) => Number(a) - Number(b));
    if (deptObj.isTimber && activeWidthKeys.length === 0) {
      activeWidthKeys.push("4", "5");
    }

    const totalAmountSum = finalRows.reduce((acc, r) => acc + (r.total_amount || 0), 0);
    const totalCftSum = finalRows.reduce((acc, r) => acc + (r.cft || 0), 0);
    const totalPcsSum = finalRows.reduce((acc, r) => acc + (r.pcs || 0), 0);

    return { requirementsData: finalRows, activeWidthKeys, totalAmountSum, totalCftSum, totalPcsSum };
  };

  const activeDepts = selectedDept === "all"
    ? DEPARTMENTS.filter((d) => d.key !== "all")
    : DEPARTMENTS.filter((d) => d.key === selectedDept);

  const getPurchaseTimberQty = (woodId: number, woodTypeId: number) => {
    let total = 0;
    matchedPoBills.forEach((bill: any) => {
      (bill.po_details || []).forEach((item: any) => {
        if (Number(item.wood_id) === Number(woodId) && Number(item.pbg_wood_type) === Number(woodTypeId)) {
          total += Number(item.pbg_cft || item.pbg_qty || 0);
        }
      });
    });
    return parseFloat(total.toFixed(4));
  };

  const getPurchaseStoreQty = (productId: number) => {
    let total = 0;
    matchedPoBills.forEach((bill: any) => {
      (bill.po_details || []).forEach((item: any) => {
        if (Number(item.pbg_description) === Number(productId)) {
          total += Number(item.pbg_qty || 0);
        }
      });
    });
    return parseFloat(total.toFixed(3));
  };

  const handleExportExcelSingleSheet = (mode: 'requirement' | 'purchase' | 'issue') => {
    if (!detail) return;
    const aoa: any[][] = [];
    const merges: any[] = [];
    let currentRow = 0;

    const processDeptData = (deptObj: any, customLabel?: string) => {
      const { requirementsData, activeWidthKeys } = computeDeptData(deptObj);
      const data = requirementsData.filter(r => !removedRowKeys.has(r.key));
      if (data.length === 0) return;

      const sumAmt = data.reduce((acc, r) => acc + (r.total_amount || 0), 0);
      const sumCft = data.reduce((acc, r) => acc + (r.cft || 0), 0);
      const sumPcs = data.reduce((acc, r) => acc + (r.pcs || 0), 0);

      // Section title
      aoa.push([customLabel || deptObj.label.toUpperCase()]);

      const totalCols = deptObj.isTimber ? (2 + activeWidthKeys.length + (mode === "requirement" ? 4 : 5)) : (mode === "requirement" ? 5 : 6);
      merges.push({
        s: { r: currentRow, c: 0 },
        e: { r: currentRow, c: totalCols - 1 }
      });

      aoa.push([]); // blank row
      currentRow += 2;

      if (deptObj.isTimber) {
        const headers = ["PO No.", "Size/Wood Type", "Mix"];
        activeWidthKeys.forEach((w) => headers.push(`${w}"`));
        headers.push("CFT");
        if (mode === "issue") {
          headers.push("Issued");
        } else if (mode === "purchase") {
          headers.push("Purchase");
        }
        headers.push("Pcs", "Price", "Total Amt");

        aoa.push(headers);
        currentRow += 1;

        data.forEach((r, idx) => {
          const poNo = `Exp. ${detail.pi_order_no}/${String(idx + 1).padStart(2, '0')}`;
          const rowVals = [
            poNo,
            r.size_wood_type,
            r.mix || "",
            ...activeWidthKeys.map((w) => {
              const req = r.widths?.[w];
              const iss = r.issued_widths?.[w] || 0;
              if (iss > 0 && req) return `${req}/${iss}`;
              if (iss > 0) return `${iss}`;
              return req || "";
            }),
            r.cft
          ];
          if (mode === "issue") {
            rowVals.push(r.issued_item && r.issued_item !== "Not Issued" ? r.issued_item : "0");
          } else if (mode === "purchase") {
            const woodId = r.wood_id || (woods.find((w: any) => `${w.wood_length}' x ${w.wood_thickness}"` === r.size_wood_type.split(' (')[0])?.wood_id);
            const woodTypeObj = woodTypes.find((wt: any) => r.size_wood_type.includes(`(${wt.wood_name.charAt(0).toUpperCase()})`));
            const purQty = getPurchaseTimberQty(woodId, woodTypeObj?.wood_id || 0);
            rowVals.push(purQty > 0 ? `${purQty} cft` : "0 cft");
          }
          rowVals.push(r.pcs, r.price, r.total_amount);
          aoa.push(rowVals);
          currentRow += 1;
        });

        // Grand Total
        const footer: (string | number)[] = ["Grand Total", "", ""];
        activeWidthKeys.forEach(() => footer.push(""));
        footer.push(parseFloat(sumCft.toFixed(4))); // CFT
        if (mode === "issue" || mode === "purchase") {
          footer.push("");
        }
        footer.push(sumPcs, "", parseFloat(sumAmt.toFixed(4)));
        aoa.push(footer);
        currentRow += 1;
      } else {
        const headers = ["PO No.", "Product Name", "Qty"];
        if (mode === "issue") {
          headers.push("Issued");
        } else if (mode === "purchase") {
          headers.push("Purchase");
        }
        headers.push("Price", "Total Amt");
        aoa.push(headers);
        currentRow += 1;

        data.forEach((r, idx) => {
          const poNo = `Exp. ${detail.pi_order_no}/${String(idx + 1).padStart(2, '0')}`;
          const rowVals = [
            poNo,
            r.product_name,
            `${r.qty}${r.unit_name ? ` ${r.unit_name}` : ""}`
          ];
          if (mode === "issue") {
            rowVals.push(r.issued_item && r.issued_item !== "Not Issued" ? r.issued_item : "0");
          } else if (mode === "purchase") {
            const prodId = r.product_id || (detail.items.find((item: any) => item.product_name === r.product_name)?.pid_product_id);
            const purQty = getPurchaseStoreQty(prodId);
            rowVals.push(purQty > 0 ? `${purQty}` : "0");
          }
          rowVals.push(r.price, r.total_amount);
          aoa.push(rowVals);
          currentRow += 1;
        });

        const footer: (string | number)[] = ["Grand Total", "", ""];
        if (mode === "issue" || mode === "purchase") {
          footer.push("");
        }
        footer.push("", parseFloat(sumAmt.toFixed(4)));
        aoa.push(footer);
        currentRow += 1;
      }

      aoa.push([]);
      aoa.push([]);
      currentRow += 2;
    };

    activeDepts.forEach((deptObj) => {
      if (deptObj.showTimber) {
        processDeptData({ key: "timber", label: "Timber", isTimber: true }, `${deptObj.label.toUpperCase()} - TIMBER`);
        processDeptData(deptObj, `${deptObj.label.toUpperCase()} - STORE ITEMS`);
      } else {
        processDeptData(deptObj);
      }
    });

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(aoa);
    worksheet["!merges"] = merges;

    // Apply basic font styles (bold headers)
    for (let r = 0; r < aoa.length; r++) {
      const firstCell = XLSX.utils.encode_cell({ r, c: 0 });
      if (worksheet[firstCell] && typeof worksheet[firstCell].v === 'string') {
        const cellVal = worksheet[firstCell].v;
        if (cellVal.includes("TIMBER") || cellVal.includes("MACHINERY") || cellVal.includes("ASSEMBLY") || cellVal.includes("SANDING") || cellVal.includes("FINISHING") || cellVal.includes("PACKED")) {
          worksheet[firstCell].s = {
            font: { bold: true, sz: 14, color: { rgb: "000000" } }
          };
        }
      }
    }

    XLSX.utils.book_append_sheet(workbook, worksheet, "Requirements");

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `${mode.charAt(0).toUpperCase() + mode.slice(1)}_${selectedDept === "all" ? "All_Departments" : selectedDept}_PI_${detail.pi_order_no}_${dayjs().format("YYYYMMDD")}.xlsx`
    );
  };

  const handleExportExcel = () => {
    const workbook = XLSX.utils.book_new();

    activeDepts.forEach((deptObj) => {
      const aoa: any[][] = [];
      const merges: any[] = [];

      const appendDeptDataToAOA = (targetDeptObj: any, titleSuffix?: string) => {
        const { requirementsData, activeWidthKeys, totalAmountSum, totalCftSum, totalPcsSum } = computeDeptData(targetDeptObj);
        if (requirementsData.length === 0) return;

        aoa.push([titleSuffix ? `${deptObj.label} - ${titleSuffix}` : deptObj.label]);
        aoa.push([]);

        const startRow = aoa.length;

        if (targetDeptObj.isTimber) {
          const rowWidthHeader = ["", ""];
          activeWidthKeys.forEach((_, idx) => {
            rowWidthHeader.push(idx === 0 ? "Width" : "");
          });
          rowWidthHeader.push("", "", "", "", "");
          aoa.push(rowWidthHeader);

          if (activeWidthKeys.length > 1) {
            merges.push({
              s: { r: startRow, c: 2 },
              e: { r: startRow, c: 2 + activeWidthKeys.length - 1 },
            });
          }

          const rowColHeaders = [
            "Size/Wood Type",
            "Mix",
            ...activeWidthKeys.map((w) => `${w}"`),
            "CFT",
            "Issued",
            "Pcs",
            "Price",
            "Total Amt",
          ];
          aoa.push(rowColHeaders);

          requirementsData.forEach((r) => {
            const rowVals = [
              r.size_wood_type,
              r.mix || "",
              ...activeWidthKeys.map((w) => {
                const req = r.widths?.[w];
                const iss = r.issued_widths?.[w] || 0;
                if (iss > 0 && req) return `${req}/${iss}`;
                if (iss > 0) return `${iss}`;
                return req || "";
              }),
              r.cft,
              r.issued_item && r.issued_item !== "Not Issued" ? r.issued_item : "0",
              r.pcs,
              r.price,
              r.total_amount,
            ];
            aoa.push(rowVals);
          });

          const footerRow: (string | number)[] = ["Grand Total", ""];
          activeWidthKeys.forEach(() => footerRow.push(""));
          footerRow.push(parseFloat(totalCftSum.toFixed(4)), "", totalPcsSum, "", parseFloat(totalAmountSum.toFixed(4)));
          aoa.push(footerRow);
        } else {
          aoa.push(["Product Name", "Qty", "Issued", "Price", "Total Amt"]);

          requirementsData.forEach((r) => {
            aoa.push([r.product_name, r.qty, r.issued_item && r.issued_item !== "Not Issued" ? r.issued_item : "0", r.price, r.total_amount]);
          });

          const nonTimberFooterRow: (string | number)[] = ["", "", "", "Grand Total", parseFloat(totalAmountSum.toFixed(4))];
          aoa.push(nonTimberFooterRow);
        }
        aoa.push([]);
        aoa.push([]);
      };

      if (deptObj.showTimber) {
        appendDeptDataToAOA({ key: "timber", label: "Timber", isTimber: true }, "Timber");
        appendDeptDataToAOA(deptObj, "Store Items");
      } else {
        appendDeptDataToAOA(deptObj);
      }

      if (aoa.length === 0) return;

      const worksheet = XLSX.utils.aoa_to_sheet(aoa);
      if (merges.length > 0) {
        worksheet["!merges"] = merges;
      }
      XLSX.utils.book_append_sheet(workbook, worksheet, deptObj.label);
    });

    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `Requirements_${selectedDept === "all" ? "All_Departments" : selectedDept}_PI_${detail?.pi_order_no || ""}_${dayjs().format("YYYYMMDD")}.xlsx`
    );
  };

  const getDeptName = (deptId: number) => {
    const dept = allDepts.find((d: any) => d.department_id === deptId);
    return dept ? dept.department_name : `Department ${deptId}`;
  };

  const getEmpName = (empId: number) => {
    const emp = employees.find((e: any) => e.employee_id === empId);
    return emp ? `${emp.employee_first_name} ${emp.employee_last_name}` : `Employee ${empId}`;
  };

  const getProductTitle = (pidDetailId: number) => {
    const item = detail?.items?.find((i: any) => i.pid_id === pidDetailId);
    return item ? item.product_name : `Product ID: ${pidDetailId}`;
  };

  const renderTableElement = (deptObj: any, mode: 'requirement' | 'purchase' | 'issue') => {
    const { requirementsData, activeWidthKeys } = computeDeptData(deptObj);
    const data = requirementsData.filter(r => !removedRowKeys.has(r.key));

    const columns: any[] = [
      {
        title: "PO No.",
        key: "po_no",
        width: 120,
        render: (_: any, __: any, index: number) => `Exp. ${detail?.pi_order_no}/${String(index + 1).padStart(2, '0')}`,
      }
    ];

    if (deptObj.isTimber) {
      columns.push(
        {
          title: "Size/Wood Type",
          dataIndex: "size_wood_type",
          key: "size_wood_type",
          width: 170,
        },
        {
          title: "Mix",
          dataIndex: "mix",
          key: "mix",
          width: 80,
          render: (val: any) => val || "-",
        },
        {
          title: "Width",
          children: activeWidthKeys.map((w) => ({
            title: `${w}"`,
            key: `width_${w}`,
            width: 75,
            align: "center" as const,
            render: (_: any, record: any) => {
              const reqQty = record.widths?.[w];
              const issuedQty = record.issued_widths?.[w] || 0;

              if (issuedQty > 0 && reqQty !== undefined && reqQty !== null && reqQty !== 0) {
                const color = getIssuedColor(issuedQty, reqQty);
                return (
                  <span>
                    <span>{reqQty}</span>
                    <span style={{ color: "#8c8c8c" }}>/</span>
                    <span style={{ color, fontWeight: 700 }}>{issuedQty}</span>
                  </span>
                );
              } else if (issuedQty > 0) {
                const color = getIssuedColor(issuedQty, 0);
                return (
                  <span style={{ color, fontWeight: 700 }}>{issuedQty}</span>
                );
              } else if (reqQty !== undefined && reqQty !== null && reqQty !== 0) {
                return <span>{reqQty}</span>;
              }
              return <span style={{ color: "#bfbfbf" }}>-</span>;
            },
          })),
        },
        {
          title: "CFT",
          dataIndex: "cft",
          key: "cft",
          width: 90,
          render: (cft: number) => <strong>{cft}</strong>,
        }
      );

      if (mode === "issue") {
        columns.push({
          title: "Issued",
          dataIndex: "issued_item",
          key: "issued_item",
          width: 120,
          render: (val: any, record: any) => {
            const reqCft = record.cft || 0;
            const issuedCft = record.issued_cft || 0;
            const color = getIssuedColor(issuedCft, reqCft);
            return (
              <span style={{ fontWeight: 600, color }}>
                {val && val !== 'Not Issued' ? val : "0"}
              </span>
            );
          }
        });
      } else if (mode === "purchase") {
        columns.push({
          title: "Purchase",
          key: "purchase_item",
          width: 120,
          render: (_: any, record: any) => {
            const woodId = record.wood_id || (woods.find((w: any) => `${w.wood_length}' x ${w.wood_thickness}"` === record.size_wood_type.split(' (')[0])?.wood_id);
            const woodTypeObj = woodTypes.find((wt: any) => record.size_wood_type.includes(`(${wt.wood_name.charAt(0).toUpperCase()})`));
            const purQty = getPurchaseTimberQty(woodId, woodTypeObj?.wood_id || 0);
            return (
              <span style={{ fontWeight: 600, color: purQty > 0 ? '#1677ff' : '#8c8c8c' }}>
                {purQty > 0 ? `${purQty} cft` : "0 cft"}
              </span>
            );
          }
        });
      }

      columns.push(
        {
          title: "Pcs",
          dataIndex: "pcs",
          key: "pcs",
          width: 80,
          render: (pcs: number) => pcs || "-",
        },
        {
          title: "Price",
          dataIndex: "price",
          key: "price",
          width: 100,
          render: (price: number) => `${price}`,
        },
        {
          title: "Total Amt",
          dataIndex: "total_amount",
          key: "total_amount",
          width: 120,
          render: (amt: number) => (
            <strong style={{ color: "#1a1a1a" }}>{formatPrice(amt)}</strong>
          ),
        }
      );
    } else {
      columns.push(
        {
          title: "Product Name",
          dataIndex: "product_name",
          key: "product_name",
        },
        {
          title: "Qty",
          dataIndex: "qty",
          key: "qty",
          width: 120,
          render: (qty: number, record: any) =>
            `${parseFloat(Number(qty || 0).toFixed(3))}${record.unit_name ? ` ${record.unit_name}` : ""}`,
        }
      );

      if (mode === "issue") {
        columns.push({
          title: "Issued",
          dataIndex: "issued_item",
          key: "issued_item",
          width: 120,
          render: (val: any, record: any) => {
            const reqQty = record.qty || 0;
            const issuedQty = Number(val) || 0;
            const color = getIssuedColor(issuedQty, reqQty);
            return (
              <span style={{ fontWeight: 600, color }}>
                {val && val !== 'Not Issued' ? val : "0"}
              </span>
            );
          }
        });
      } else if (mode === "purchase") {
        columns.push({
          title: "Purchase",
          key: "purchase_item",
          width: 120,
          render: (_: any, record: any) => {
            const prodId = record.product_id || (detail?.items?.find((item: any) => item.product_name === record.product_name)?.pid_product_id);
            const purQty = getPurchaseStoreQty(prodId);
            return (
              <span style={{ fontWeight: 600, color: purQty > 0 ? '#1677ff' : '#8c8c8c' }}>
                {purQty > 0 ? `${purQty}` : "0"}
              </span>
            );
          }
        });
      }

      columns.push(
        {
          title: "Pcs",
          dataIndex: "pcs",
          key: "pcs",
          width: 80,
          render: (pcs: number) => pcs || "-",
        },
        {
          title: "Price",
          dataIndex: "price",
          key: "price",
          width: 100,
          render: (price: number) => `${price}`,
        },
        {
          title: "Total Amt",
          dataIndex: "total_amount",
          key: "total_amount",
          width: 120,
          render: (amt: number) => (
            <strong style={{ color: "#1a1a1a" }}>{formatPrice(amt)}</strong>
          ),
        }
      );
    }

    columns.push({
      title: "Actions",
      key: "actions",
      width: 90,
      align: "center",
      render: (_: any, record: any) => (
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveRow(record.key)}
        />
      )
    });

    const sumAmt = data.reduce((acc, r) => acc + (r.total_amount || 0), 0);
    const sumCft = data.reduce((acc, r) => acc + (r.cft || 0), 0);
    const sumPcs = data.reduce((acc, r) => acc + (r.pcs || 0), 0);
    const hasModeCol = mode === "issue" || mode === "purchase";

    return (
      <Table
        dataSource={data}
        columns={columns as any}
        pagination={false}
        size="small"
        bordered
        summary={() => {
          if (deptObj.isTimber) {
            const preCftSpan = 3 + activeWidthKeys.length;
            return (
              <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={preCftSpan} align="right">
                  Grand Total
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <strong style={{ color: "#1a1a1a" }}>{parseFloat(sumCft.toFixed(4))}</strong>
                </Table.Summary.Cell>
                {hasModeCol && <Table.Summary.Cell index={2} />}
                <Table.Summary.Cell index={hasModeCol ? 3 : 2}>
                  <strong style={{ color: "#1a1a1a" }}>{sumPcs}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={hasModeCol ? 4 : 3} />
                <Table.Summary.Cell index={hasModeCol ? 5 : 4}>
                  <strong style={{ color: "#1a1a1a" }}>{formatPrice(sumAmt)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={hasModeCol ? 6 : 5} />
              </Table.Summary.Row>
            );
          }

          const prePcsSpan = hasModeCol ? 4 : 3;
          return (
            <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={prePcsSpan} align="right">
                Grand Total
              </Table.Summary.Cell>
              <Table.Summary.Cell index={1}>
                <strong style={{ color: "#1a1a1a" }}>{sumPcs > 0 ? sumPcs : "-"}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3}>
                <strong style={{ color: "#1a1a1a" }}>{formatPrice(sumAmt)}</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={4} />
            </Table.Summary.Row>
          );
        }}
      />
    );
  };

  const renderDeptTable = (deptObj: any, mode: 'requirement' | 'purchase' | 'issue') => {
    if (deptObj.showTimber) {
      const timberDept = { key: "timber", label: "Timber", isTimber: true };
      return (
        <div key={deptObj.key} style={{ marginBottom: 32 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginBottom: 12 }}>
            {deptObj.label}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1677ff", marginBottom: 8, paddingLeft: 8 }}>
            Timber (Wood)
          </div>
          <div style={{ marginBottom: 16 }}>
            {renderTableElement(timberDept, mode)}
          </div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#1677ff", marginBottom: 8, paddingLeft: 8 }}>
            Store Items
          </div>
          <div>
            {renderTableElement(deptObj, mode)}
          </div>
        </div>
      );
    }

    return (
      <div key={deptObj.key} style={{ marginBottom: 32 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#1a1a1a", marginBottom: 12 }}>
          {deptObj.label}
        </div>
        {renderTableElement(deptObj, mode)}
      </div>
    );
  };

  const renderRequirementModal = () => (
    <Modal
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "95%" }}>
          <div>
            <span style={{ fontSize: 18, fontWeight: 700 }}>Material & Component Requirements</span>
            <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>Requirements list with PO Numbers (Excludes Issued column)</div>
          </div>
          <Space>
            <Button onClick={handleResetRows} style={{ borderRadius: 6 }}>Reset Deleted Rows</Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() => handleExportExcelSingleSheet("requirement")}
              style={{ borderRadius: 6, fontWeight: 600, backgroundColor: "#107c41", borderColor: "#107c41" }}
            >
              Export Excel (.xlsx)
            </Button>
          </Space>
        </div>
      }
      open={requirementModalVisible}
      onCancel={() => setRequirementModalVisible(false)}
      footer={null}
      width="90%"
      style={{ top: 20 }}
    >
      <div style={{ marginTop: 20 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 600 }}>Filter Department:</span>
          <Select
            value={selectedDept}
            onChange={(val) => setSelectedDept(val)}
            style={{ width: 180 }}
            options={DEPARTMENTS.map((d) => ({ label: d.label, value: d.key }))}
          />
        </div>
        {activeDepts.map((deptObj) => renderDeptTable(deptObj, "requirement"))}
      </div>
    </Modal>
  );

  const renderTrackPurchaseModal = () => (
    <Modal
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "95%" }}>
          <div>
            <span style={{ fontSize: 18, fontWeight: 700 }}>Track Purchase</span>
            <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>Purchase quantities tracked from Purchase Bills matching PI #{detail?.pi_order_no}</div>
          </div>
          <Space>
            <Button onClick={handleResetRows} style={{ borderRadius: 6 }}>Reset Deleted Rows</Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() => handleExportExcelSingleSheet("purchase")}
              style={{ borderRadius: 6, fontWeight: 600, backgroundColor: "#107c41", borderColor: "#107c41" }}
            >
              Export Excel (.xlsx)
            </Button>
          </Space>
        </div>
      }
      open={trackPurchaseModalVisible}
      onCancel={() => setTrackPurchaseModalVisible(false)}
      footer={null}
      width="90%"
      style={{ top: 20 }}
    >
      <div style={{ marginTop: 20 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 600 }}>Filter Department:</span>
          <Select
            value={selectedDept}
            onChange={(val) => setSelectedDept(val)}
            style={{ width: 180 }}
            options={DEPARTMENTS.map((d) => ({ label: d.label, value: d.key }))}
          />
        </div>
        {activeDepts.map((deptObj) => renderDeptTable(deptObj, "purchase"))}
      </div>
    </Modal>
  );

  const renderTrackIssueModal = () => (
    <Modal
      title={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "95%" }}>
          <div>
            <span style={{ fontSize: 18, fontWeight: 700 }}>Track Issue</span>
            <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>Issued material quantities (Contains Issued column)</div>
          </div>
          <Space>
            <Button onClick={handleResetRows} style={{ borderRadius: 6 }}>Reset Deleted Rows</Button>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() => handleExportExcelSingleSheet("issue")}
              style={{ borderRadius: 6, fontWeight: 600, backgroundColor: "#107c41", borderColor: "#107c41" }}
            >
              Export Excel (.xlsx)
            </Button>
          </Space>
        </div>
      }
      open={trackIssueModalVisible}
      onCancel={() => setTrackIssueModalVisible(false)}
      footer={null}
      width="90%"
      style={{ top: 20 }}
    >
      <div style={{ marginTop: 20 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 20 }}>
          <span style={{ fontWeight: 600 }}>Filter Department:</span>
          <Select
            value={selectedDept}
            onChange={(val) => setSelectedDept(val)}
            style={{ width: 180 }}
            options={DEPARTMENTS.map((d) => ({ label: d.label, value: d.key }))}
          />
        </div>
        {activeDepts.map((deptObj) => renderDeptTable(deptObj, "issue"))}
      </div>
    </Modal>
  );

  const renderTrackWorkOrderModal = () => (
    <Modal
      title={<span style={{ fontSize: 18, fontWeight: 700 }}>Track Work Orders (PI #{detail?.pi_order_no})</span>}
      open={trackWorkOrderModalVisible}
      onCancel={() => setTrackWorkOrderModalVisible(false)}
      footer={null}
      width={1100}
      centered
    >
      <div style={{ marginTop: 20, maxHeight: "70vh", overflowY: "auto", paddingRight: 10 }}>
        {Object.keys(workOrdersByDept).length === 0 ? (
          <div style={{ textAlign: "center", padding: 40, color: "#999" }}>No work orders assigned for this PI.</div>
        ) : (
          Object.entries(workOrdersByDept).map(([deptIdStr, wos]) => {
            const deptId = Number(deptIdStr);
            const deptName = deptId === 0 ? "Unassigned Department" : getDeptName(deptId);

            const columns = [
              {
                title: "WO Number",
                dataIndex: "wo_number",
                key: "wo_number",
                width: 150,
                render: (val: string) => <strong>{val}</strong>
              },
              {
                title: "Product Name",
                dataIndex: "wo_pid_detail_id",
                key: "product_name",
                render: (val: number) => getProductTitle(val)
              },
              {
                title: "Employee",
                dataIndex: "wo_employee_id",
                key: "employee",
                width: 200,
                render: (val: number) => getEmpName(val)
              },
              {
                title: "Quantity",
                dataIndex: "wo_qty",
                key: "qty",
                width: 110,
              },
              {
                title: "Due Date",
                dataIndex: "wo_due_date",
                key: "due_date",
                width: 130,
              },
              {
                title: "Status",
                dataIndex: "wo_status",
                key: "status",
                width: 120,
                render: (status: string) => (
                  <Tag color={status === 'completed' ? 'green' : 'gold'}>
                    {status ? status.toUpperCase() : 'PENDING'}
                  </Tag>
                )
              }
            ];

            return (
              <div key={deptId} style={{ marginBottom: 24 }}>
                <div style={{
                  fontSize: 15,
                  fontWeight: 700,
                  marginBottom: 10,
                  color: "#1677ff",
                  borderBottom: "2px solid #e6f7ff",
                  paddingBottom: 6
                }}>
                  {deptName}
                </div>
                <Table
                  dataSource={wos}
                  columns={columns}
                  pagination={false}
                  size="small"
                  bordered
                  rowKey="wo_id"
                />
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );

  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item href="/">
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <span
            onClick={() => navigate("/track-pi")}
            style={{ cursor: "pointer", color: "#1677ff" }}
          >
            Track PI (Performa Invoices)
          </span>
        </Breadcrumb.Item>
        <Breadcrumb.Item>PI #{detail.pi_order_no}</Breadcrumb.Item>
      </Breadcrumb>

      {/* ── PI Info Card (top) ── */}
      <div
        style={{
          border: `3px solid ${borderColor}`,
          borderRadius: 12,
          padding: "16px 24px",
          background: "#fff",
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 28,
          boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
          width: "100%",
          gap: 16,
        }}
      >
        {/* PI Number */}
        <div style={{ flex: "1 1 120px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
            PI Number
          </div>
          <div style={{ fontSize: 24, fontWeight: 700, lineHeight: 1 }}>
            #{detail.pi_order_no}
          </div>
          <div style={{ fontSize: 12, color: "#bbb", marginTop: 2 }}>
            PI-{detail.pi_id}
          </div>
        </div>

        <div
          style={{ width: 1, height: 48, background: "#f0f0f0", flexShrink: 0 }}
        />

        {/* Client */}
        <div style={{ flex: "2 1 160px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
            Client
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {detail.ledger_company_name ||
              `${detail.ledger_first_name} ${detail.ledger_last_name}`}
          </div>
        </div>

        <div
          style={{ width: 1, height: 48, background: "#f0f0f0", flexShrink: 0 }}
        />

        {/* PI Date */}
        <div style={{ flex: "1 1 120px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
            PI Date
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {dayjs(detail.pi_date).format("DD MMM YYYY")}
          </div>
        </div>

        <div
          style={{ width: 1, height: 48, background: "#f0f0f0", flexShrink: 0 }}
        />

        {/* Deadline */}
        <div style={{ flex: "1 1 140px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 2 }}>
            Deadline
          </div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>
            {deadline.format("DD MMM YYYY")}
          </div>
          <div style={{ fontSize: 11, color: "#aaa" }}>
            Lead time: {detail.pi_lead_time} days
          </div>
        </div>

        <div
          style={{ width: 1, height: 48, background: "#f0f0f0", flexShrink: 0 }}
        />

        {/* Status */}
        <div style={{ flex: "1 1 100px" }}>
          <div style={{ fontSize: 11, color: "#999", marginBottom: 6 }}>
            Status
          </div>
          <Tag
            color={tagColor}
            style={{ fontWeight: 600, borderRadius: 20, fontSize: 12 }}
          >
            {statusText}
          </Tag>
        </div>
      </div>

      {/* ── Section Title & Action Buttons ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: "#333",
          }}
        >
          Products ({detail.items.length})
        </div>

        <Space wrap>
          <Button
            type="primary"
            onClick={() => setRequirementModalVisible(true)}
            style={{ borderRadius: 6, fontWeight: 600 }}
          >
            Requirement
          </Button>
          <Button
            type="primary"
            onClick={() => setTrackPurchaseModalVisible(true)}
            style={{ borderRadius: 6, fontWeight: 600 }}
          >
            Track Purchase
          </Button>
          <Button
            type="primary"
            onClick={() => setTrackIssueModalVisible(true)}
            style={{ borderRadius: 6, fontWeight: 600 }}
          >
            Track Issue
          </Button>
          <Button
            type="primary"
            onClick={() => setTrackWorkOrderModalVisible(true)}
            style={{ borderRadius: 6, fontWeight: 600 }}
          >
            Track Work Order
          </Button>
        </Space>
      </div>

      {/* ── Product Cards ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
        {detail.items.length === 0 && (
          <div style={{ color: "#aaa" }}>No products found</div>
        )}

        {detail.items.map((item) => {
          const isDone =
            item.total_qty > 0 &&
            Number(item.ready_qty) >= Number(item.total_qty);
          const percent =
            item.total_qty > 0
              ? Math.min(
                Math.round(
                  (Number(item.ready_qty) / Number(item.total_qty)) * 100,
                ),
                100,
              )
              : 0;

          return (
            <div
              key={item.pid_id}
              onClick={() =>
                navigate(`/track-pi/${pi_id}/${item.pid_id}`)
              }
              style={{
                width: 240,
                border: `2px solid ${isDone ? "#52c41a" : "#e8e8e8"}`,
                borderRadius: 12,
                padding: "16px 18px",
                background: "#fff",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                transition: "transform 0.15s, box-shadow 0.15s",
                position: "relative",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform =
                  "translateY(-3px)";
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  "0 6px 16px rgba(0,0,0,0.12)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLDivElement).style.transform =
                  "translateY(0)";
                (e.currentTarget as HTMLDivElement).style.boxShadow =
                  "0 2px 8px rgba(0,0,0,0.06)";
              }}
            >
              {/* Green tick */}
              {isDone && (
                <CheckCircleFilled
                  style={{
                    position: "absolute",
                    top: 12,
                    right: 12,
                    color: "#52c41a",
                    fontSize: 18,
                  }}
                />
              )}

              {/* Product Name */}
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "#1a1a1a",
                  marginBottom: 12,
                  paddingRight: isDone ? 26 : 0,
                  lineHeight: 1.4,
                }}
              >
                {item.product_name}
              </div>

              <div
                style={{ borderTop: "1px solid #f5f5f5", marginBottom: 10 }}
              />

              {/* Total QTY */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 6,
                }}
              >
                <span style={{ fontSize: 12, color: "#888" }}>Total QTY</span>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {item.total_qty}
                </span>
              </div>

              {/* Ready QTY */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: 14,
                }}
              >
                <span style={{ fontSize: 12, color: "#888" }}>Ready QTY</span>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: isDone
                      ? "#52c41a"
                      : Number(item.ready_qty) > 0
                        ? "#1677ff"
                        : "#bbb",
                  }}
                >
                  {item.ready_qty}
                </span>
              </div>

              {/* Progress Bar */}
              <div
                style={{
                  background: "#f0f0f0",
                  borderRadius: 10,
                  height: 6,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${percent}%`,
                    height: "100%",
                    background: isDone ? "#52c41a" : "#1677ff",
                    borderRadius: 10,
                    transition: "width 0.4s ease",
                  }}
                />
              </div>
              <div
                style={{
                  textAlign: "right",
                  fontSize: 11,
                  color: "#bbb",
                  marginTop: 4,
                }}
              >
                {percent}%
              </div>
            </div>
          );
        })}
      </div>

      {/* Render the Modals */}
      {renderRequirementModal()}
      {renderTrackPurchaseModal()}
      {renderTrackIssueModal()}
      {renderTrackWorkOrderModal()}
    </div>
  );
};

export default TrackPIDetail;
